import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ColorSchemePreference = "light" | "dark" | "system";
export type ResolvedColorScheme = "light" | "dark";

const STORAGE_KEY = "ming.color-scheme";

/**
 * Three viewer states have to resolve correctly (docs/design/README.md):
 * an explicit light choice, an explicit dark choice, and the system default.
 *
 * "system" removes the `data-theme` attribute entirely, which is what lets the
 * `prefers-color-scheme` block in the generated theme stylesheet take over.
 */
function applyPreference(preference: ColorSchemePreference): void {
  const root = document.documentElement;
  if (preference === "system") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", preference);
  }
}

export function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolveScheme(preference: ColorSchemePreference): ResolvedColorScheme {
  if (preference === "system") return systemPrefersDark() ? "dark" : "light";
  return preference;
}

interface ColorSchemeState {
  preference: ColorSchemePreference;
  resolved: ResolvedColorScheme;
  setPreference: (preference: ColorSchemePreference) => void;
  syncSystem: () => void;
}

export const useColorScheme = create<ColorSchemeState>()(
  persist(
    (set, get) => ({
      preference: "system",
      resolved: "light",
      setPreference: (preference) => {
        applyPreference(preference);
        set({ preference, resolved: resolveScheme(preference) });
      },
      syncSystem: () => {
        set({ resolved: resolveScheme(get().preference) });
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ preference: state.preference }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        applyPreference(state.preference);
        state.syncSystem();
      },
    },
  ),
);

/** Applied before React mounts so the first paint is already correct. */
export function initColorScheme(): void {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  let preference: ColorSchemePreference = "system";
  if (stored) {
    try {
      const parsed: unknown = JSON.parse(stored);
      const candidate = (parsed as { state?: { preference?: unknown } })?.state?.preference;
      if (candidate === "light" || candidate === "dark" || candidate === "system") {
        preference = candidate;
      }
    } catch {
      preference = "system";
    }
  }
  applyPreference(preference);

  const media = window.matchMedia("(prefers-color-scheme: dark)");
  media.addEventListener("change", () => {
    useColorScheme.getState().syncSystem();
  });
}
